import React, { useState, useEffect } from 'react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'
import BlocoInteligenciaM4 from './components/BlocoInteligenciaM4'
import BlocoPrioridades from './components/BlocoPrioridades'
import TabelaClientes from './components/TabelaClientes'
import FiltrosClientes from './components/FiltrosClientes'

const InteligenciaMercadoPage: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [periodo, setPeriodo] = useState(() => {
    const hoje = new Date()
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`
  })
  
  const [dados, setDados] = useState<any>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<any>(null)
  
  // Estados para camada operacional
  const [dadosOperacionais, setDadosOperacionais] = useState<any>(null)
  const [loadingOperacional, setLoadingOperacional] = useState(true)
  const [segmentoFiltro, setSegmentoFiltro] = useState('todos')
  const [statusFiltro, setStatusFiltro] = useState('todos')
  const [qualidadeFiltro, setQualidadeFiltro] = useState('todos')

  useEffect(() => {
    fetchDados()
    fetchLastRefresh()
    fetchDadosOperacionais()
  }, [periodo, segmentoFiltro, statusFiltro])

  const fetchDadosOperacionais = async () => {
    setLoadingOperacional(true)
    
    try {
      const params = new URLSearchParams({
        periodo: periodo,
        ...(segmentoFiltro !== 'todos' && { segmento: segmentoFiltro }),
        ...(statusFiltro !== 'todos' && { status: statusFiltro })
      })
      
      const response = await fetch(`/api/gestao/inteligencia-mercado/clientes-detalhados?${params}`)
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar dados operacionais: ${response.statusText}`)
      }
      
      const result = await response.json()
      setDadosOperacionais(result)
    } catch (err: any) {
      console.error('Erro ao buscar dados operacionais:', err)
    } finally {
      setLoadingOperacional(false)
    }
  }

  const fetchLastRefresh = async () => {
    try {
      const response = await fetch('/api/gestao/inteligencia-mercado/last-refresh')
      if (response.ok) {
        const data = await response.json()
        setLastRefresh(data)
        
        // Alertar se dados desatualizados
        if (data.is_stale) {
          console.warn('⚠️ Dados desatualizados (>24h). Recomenda-se atualizar.')
        }
      }
    } catch (err) {
      console.error('Erro ao buscar último refresh:', err)
    }
  }

  const handleRefreshCalculos = async () => {
    if (refreshing) return
    
    setRefreshing(true)
    setError(null)
    
    try {
      const response = await fetch('/api/gestao/inteligencia-mercado/refresh-views', {
        method: 'POST'
      })
      
      const result = await response.json()
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao atualizar cálculos')
      }
      
      // Atualizar timestamp
      await fetchLastRefresh()
      
      // Recarregar dados
      await fetchDados()
      
      console.log(`✅ Cálculos atualizados em ${result.duration_seconds.toFixed(1)}s`)
    } catch (err: any) {
      console.error('Erro ao atualizar cálculos:', err)
      setError(err.message || 'Erro ao atualizar cálculos')
    } finally {
      setRefreshing(false)
    }
  }

  const fetchDados = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await fetch(`/api/gestao/inteligencia-mercado/m4-insights?periodo=${periodo}`)
      
      if (!response.ok) {
        throw new Error(`Erro ao buscar dados: ${response.statusText}`)
      }
      
      const data = await response.json()
      setDados(data)
    } catch (err: any) {
      console.error('Erro ao buscar insights M4:', err)
      setError(err.message || 'Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }

  const handlePeriodoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = e.target.value
    setPeriodo(`${valor}-01`)
  }

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Nunca'
    
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    
    if (diffMinutes < 1) return 'Agora mesmo'
    if (diffMinutes < 60) return `${diffMinutes} min atrás`
    if (diffHours < 24) return `${diffHours}h atrás`
    
    const diffDays = Math.floor(diffHours / 24)
    if (diffDays === 1) return 'Ontem'
    if (diffDays < 7) return `${diffDays} dias atrás`
    
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <span className="text-4xl mr-3">🎯</span>
              Inteligência de Mercado
            </h1>
            <p className="text-gray-600 mt-1">
              Análise estratégica e decisões baseadas em dados
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Timestamp da última atualização */}
            {lastRefresh && (
              <div className="flex items-center gap-2 text-sm">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`${lastRefresh.is_stale ? 'text-orange-600 font-medium' : 'text-gray-600'}`}>
                  {lastRefresh.is_stale && '⚠️ '}
                  Atualizado: {formatTimestamp(lastRefresh.timestamp)}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">
                Período:
              </label>
              <input
                type="month"
                value={periodo.slice(0, 7)}
                onChange={handlePeriodoChange}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            {/* Botão Atualizar Cálculos */}
            <button
              onClick={handleRefreshCalculos}
              disabled={refreshing}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Atualizar todos os cálculos de inteligência"
            >
              {refreshing ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Atualizando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  Atualizar Cálculos
                </>
              )}
            </button>
            
            {/* Botão Recarregar Dados */}
            <button
              onClick={fetchDados}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Recarregar dados da tela"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Carregando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Recarregar
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && !dados && (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <svg className="animate-spin h-12 w-12 mx-auto mb-4 text-blue-600" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-gray-600">Carregando insights...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-bold text-red-900">Erro ao carregar dados</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {!loading && dados && (
        <>
          {/* Camada Estratégica - M4 Intelligence */}
          <BlocoInteligenciaM4
            metricas={dados.metricas}
            topSegmentos={dados.top_segmentos}
            insights={dados.insights}
          />

          {/* Divisor */}
          <div className="my-8 border-t-4 border-gray-200"></div>

          {/* Camada Operacional - Por Cliente */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">📋</span>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Camada Operacional</h2>
                <p className="text-sm text-gray-600">Visão detalhada por cliente para ação imediata</p>
              </div>
            </div>
          </div>

          {loadingOperacional ? (
            <div className="bg-white rounded-xl shadow-lg p-12 text-center">
              <svg className="animate-spin h-12 w-12 mx-auto mb-4 text-blue-600" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className="text-gray-600">Carregando dados operacionais...</p>
            </div>
          ) : dadosOperacionais ? (
            <>
              {/* Prioridades do Mês */}
              <BlocoPrioridades
                riscoAlto={dadosOperacionais.prioridades.risco_alto}
                riscoMedio={dadosOperacionais.prioridades.risco_medio}
                oportunidades={dadosOperacionais.prioridades.oportunidades}
              />

              {/* Filtros */}
              <FiltrosClientes
                segmentoSelecionado={segmentoFiltro}
                statusSelecionado={statusFiltro}
                segmentosDisponiveis={dadosOperacionais.segmentos_disponiveis || []}
                onSegmentoChange={setSegmentoFiltro}
                onStatusChange={setStatusFiltro}
                qualidadeSelecionada={qualidadeFiltro}
                onQualidadeChange={setQualidadeFiltro}
              />

              {/* Tabela de Clientes */}
              <TabelaClientes 
                clientes={
                  qualidadeFiltro === 'todos' 
                    ? dadosOperacionais.clientes
                    : qualidadeFiltro === 'suspeitos'
                    ? dadosOperacionais.clientes.filter((c: any) => c.dados_suspeitos)
                    : dadosOperacionais.clientes.filter((c: any) => c.nivel_dados === qualidadeFiltro)
                }
                periodo={periodo}
                segmentoFiltro={segmentoFiltro}
                statusFiltro={statusFiltro}
              />
            </>
          ) : null}
          
          {/* Info Footer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Sobre os dados:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Use o botão "Atualizar Cálculos" para recalcular todas as métricas</li>
                  <li>Recomenda-se atualizar quando houver novos resultados mensais cadastrados</li>
                  <li>Apenas clientes ativos com dados válidos são considerados</li>
                  <li>Benchmarks calculados por segmento e nível de dados</li>
                  <li>Insights gerados automaticamente com base em thresholds configuráveis</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
        </div>
      </Layout>
    </ProtectedRoute>
  )
}

export default InteligenciaMercadoPage
